/**
 * PersistentTwentyCrm - Keeps Twenty CRM iframe mounted across navigation
 *
 * Mirrors PersistentGhost pattern:
 * - Mounted when user first visits /twenty-crm (lazy initialization)
 * - Kept mounted when navigating away (hidden with CSS)
 * - Shown instantly when returning to /twenty-crm
 *
 * No URL sync needed: Twenty is a full SPA with its own internal routing.
 * Unlike Ghost (hash-based routes) or Frappe CRM (PostMessage protocol),
 * Twenty handles all navigation internally via React Router.
 */

import { useLocation } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { TwentyCrmEmbed } from "@/components/TwentyCrmEmbed";
import type { TwentyCrmEmbedRef } from "@/components/TwentyCrmEmbed/types";
import { useAuthStore } from "@/lib/store/authStore";

export function PersistentTwentyCrm() {
  const location = useLocation();
  const isOnTwentyCrmPage = location.pathname.startsWith("/twenty-crm");

  // Lazy mount: only render after first visit
  const [hasVisited, setHasVisited] = useState(false);
  const [mountKey, setMountKey] = useState(0);
  const twentyRef = useRef<TwentyCrmEmbedRef>(null);

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isProvisioned = useAuthStore((state) => state.isProvisioned);

  // Reset on logout
  const wasAuthenticated = useRef(isAuthenticated);
  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated) {
      setHasVisited(false);
      setMountKey((prev) => prev + 1);
    }
    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated]);

  // Lazy mount on first visit
  useEffect(() => {
    if (isOnTwentyCrmPage && !hasVisited) {
      setHasVisited(true);
    }
  }, [isOnTwentyCrmPage, hasVisited]);

  const handleReady = useCallback(() => {
    // Twenty CRM loaded
  }, []);

  const handleError = useCallback((message: string) => {
    console.error("[PersistentTwentyCrm] Error:", message);
  }, []);

  // Don't render if never visited or not provisioned
  if (!hasVisited || !isProvisioned) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 top-0 z-10 md:left-[72px] md:right-[48px]"
      style={{
        visibility: isOnTwentyCrmPage ? "visible" : "hidden",
        pointerEvents: isOnTwentyCrmPage ? "auto" : "none",
      }}
    >
      <TwentyCrmEmbed
        ref={twentyRef}
        key={mountKey}
        className="h-full w-full"
        onReady={handleReady}
        onError={handleError}
      />
    </div>
  );
}
